import type { WidgetConfig, WidgetPreferences } from '../hooks/useWidgetPreferences';

export type DashboardRoleKey = 'admin' | 'supervisor' | 'employee' | 'client';

const CATALOG: Record<DashboardRoleKey, string[]> = {
  admin: [
    'totalRevenue',
    'monthlyRevenue',
    'pendingPayments',
    'overduePayments',
    'totalClients',
    'totalAppointments',
    'completedToday',
    'completionRate',
    'cancellationRate',
    'averageTicket',
    'employeeRankings',
    'monthlyTrend',
    'limits',
  ],
  supervisor: [
    'totalAppointments',
    'completedToday',
    'completionRate',
    'cancellationRate',
    'employeeRankings',
    'pendingPayments',
    'overduePayments',
    'limits',
  ],
  employee: ['todayAppointments', 'upcomingAppointments', 'completedToday'],
  client: ['upcomingAppointments', 'recentAppointments', 'totalServices'],
};

const PRESETS: Record<DashboardRoleKey, WidgetConfig[]> = {
  admin: [
    { id: 'totalRevenue', visible: true, order: 0 },
    { id: 'monthlyRevenue', visible: true, order: 1 },
    { id: 'pendingPayments', visible: true, order: 2 },
    { id: 'overduePayments', visible: true, order: 3 },
    { id: 'totalClients', visible: true, order: 4 },
    { id: 'totalAppointments', visible: true, order: 5 },
    { id: 'completedToday', visible: true, order: 6 },
    { id: 'completionRate', visible: true, order: 7 },
    { id: 'cancellationRate', visible: true, order: 8 },
    { id: 'averageTicket', visible: true, order: 9 },
    { id: 'employeeRankings', visible: true, order: 10 },
    { id: 'monthlyTrend', visible: true, order: 11 },
    { id: 'limits', visible: true, order: 12 },
  ],
  supervisor: [
    { id: 'totalAppointments', visible: true, order: 0 },
    { id: 'completedToday', visible: true, order: 1 },
    { id: 'completionRate', visible: true, order: 2 },
    { id: 'cancellationRate', visible: true, order: 3 },
    { id: 'employeeRankings', visible: true, order: 4 },
    { id: 'pendingPayments', visible: true, order: 5 },
    { id: 'overduePayments', visible: true, order: 6 },
    { id: 'limits', visible: true, order: 7 },
  ],
  employee: [
    { id: 'todayAppointments', visible: true, order: 0 },
    { id: 'upcomingAppointments', visible: true, order: 1 },
    { id: 'completedToday', visible: true, order: 2 },
  ],
  client: [
    { id: 'upcomingAppointments', visible: true, order: 0 },
    { id: 'recentAppointments', visible: true, order: 1 },
    { id: 'totalServices', visible: true, order: 2 },
  ],
};

export function getCatalogForRole(role: DashboardRoleKey): string[] {
  return CATALOG[role] ?? CATALOG.client;
}

export function getPresetPreferences(role: DashboardRoleKey): WidgetPreferences {
  return {
    widgets: (PRESETS[role] ?? PRESETS.client).map(w => ({ ...w })),
    layout: 'grid',
  };
}

export function mergePreferencesWithCatalog(
  role: DashboardRoleKey,
  saved: WidgetPreferences | null
): WidgetPreferences {
  const preset = getPresetPreferences(role);
  if (!saved?.widgets?.length) {
    return preset;
  }

  const allowed = new Set(getCatalogForRole(role));
  const merged = saved.widgets.filter(w => allowed.has(w.id));

  const seen = new Set(merged.map(w => w.id));
  let nextOrder = merged.length;
  for (const p of preset.widgets) {
    if (seen.has(p.id)) continue;
    merged.push({ ...p, order: nextOrder++ });
  }

  merged.sort((a, b) => a.order - b.order);
  merged.forEach((w, i) => {
    w.order = i;
  });

  return {
    layout: saved.layout === 'list' ? 'list' : 'grid',
    widgets: merged,
  };
}

export function buildStorageKey(
  dashboardRoleKey: DashboardRoleKey,
  companyId: string,
  userId: string
): string {
  return `widget_preferences_${dashboardRoleKey}_${companyId}_${userId}`;
}

export function resolveCompanyId(userCompanyId?: string): string | null {
  return userCompanyId || localStorage.getItem('default_company_id') || null;
}
